import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { first } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ContractDefinitionEditorDialog } from '../contract-definition-editor-dialog/contract-definition-editor-dialog.component';
import { ContractDefinitionService } from "../../../management-api-client";
import { ConfirmationDialogComponent, ConfirmDialogModel } from "../confirmation-dialog/confirmation-dialog.component";
import { NotificationService, SorterService, UtilService } from "../../services";
import { ContractDefinitionInput } from "../../../management-api-client/model"
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnauthorizedStateService } from 'src/modules/app/auth/unauthorized-state.service';


@Component({
  selector: 'edc-demo-contract-definition-viewer',
  templateUrl: './contract-definition-viewer.component.html',
  styleUrls: ['./contract-definition-viewer.component.scss']
})
export class ContractDefinitionViewerComponent implements OnInit, OnDestroy {
  paginationState = {
    filteredList: [] as any[],
    pagedList: [] as any[],
    pageIndex: 0,
    pageSize: 20
  };
  totalContractDefinitions = 0;
  searchText = '';
  isUnauthorized = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private contractDefinitionService: ContractDefinitionService,
    private notificationService: NotificationService,
    private readonly dialog: MatDialog,
    private readonly sorterService: SorterService,
    private readonly cdref: ChangeDetectorRef,
    private readonly unauthorizedState: UnauthorizedStateService,
    public readonly utilService: UtilService,
  ) { }

  loadContractDefinitions() {
    const { pageIndex, pageSize } = this.paginationState;
    this.contractDefinitionService.queryAllContractDefinitions({
      limit: pageSize + 1,
      offset: pageIndex * pageSize,
    }).subscribe({
      next: (contractDefinitions: any[]) => {
        const hasMore = contractDefinitions.length > pageSize;
        const page = hasMore ? contractDefinitions.slice(0, pageSize) : contractDefinitions;

        this.paginationState.pagedList = page.sort((a: any, b: any) =>
          this.sorterService.naturalSort(a.id, b.id)
        );
        this.paginationState.filteredList = this.paginationState.pagedList;
        this.totalContractDefinitions = hasMore
          ? (pageIndex + 2) * pageSize
          : pageIndex * pageSize + page.length;
      },
      error: (err: HttpErrorResponse) => {
        if (this.unauthorizedState.isUnauthorized('management')) {
          return;
        }
        this.notificationService.showError('Failed to load contract definitions');
        console.error(err);
      }
    });
  }

  ngOnInit(): void {
    this.unauthorizedState
      .isUnauthorized$('management')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUnauthorized) => {
        this.isUnauthorized = isUnauthorized;
        if (isUnauthorized) {
          this.paginationState.filteredList = [];
          this.paginationState.pagedList = [];
        }
      });

    this.loadContractDefinitions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch() {
    this.paginationState.pageIndex = 0;
    this.loadContractDefinitions();
  }

  onPageChange(event: PageEvent) {
    this.paginationState.pageIndex = event.pageIndex;
    this.paginationState.pageSize = event.pageSize;
    this.loadContractDefinitions();
  }

  onDelete(contractDefinition: any) {
    const dialogData = ConfirmDialogModel.forDelete(
      "contract definition",
      contractDefinition.id,
      "Contracts that were already negotiated through this definition remain valid; deleting it only stops new offers.");
    const ref = this.dialog.open(ConfirmationDialogComponent, {data: dialogData});

    ref.afterClosed().subscribe(res => {
      if (res) {
        this.contractDefinitionService.deleteContractDefinition(contractDefinition.id)
          .subscribe({
            next: () => {
              this.notificationService.showInfo("Contract definition deleted");
              this.loadContractDefinitions();
            },
            error: (err) => this.notificationService.showError(this.utilService.getApiErrorMessage(err) ?? "Contract definition cannot be deleted"),
          });
      }
    });
  }

  onCreate() {
    const dialogRef = this.dialog.open(ContractDefinitionEditorDialog);
    dialogRef.afterClosed().pipe(first()).subscribe((result: { contractDefinition?: ContractDefinitionInput }) => {
      const newContractDefinition = result?.contractDefinition;
      if (newContractDefinition) {
        this.contractDefinitionService.createContractDefinition(newContractDefinition)
          .subscribe({
            next: () => {
              this.notificationService.showInfo("Contract definition created");
              this.loadContractDefinitions();
            },
            error: (err) => this.notificationService.showError(this.utilService.getApiErrorMessage(err) ?? "Contract definition cannot be created"),
          });
      }
    });
  }

  onEdit(contractDefinition: any) {
    // pass a plain deep copy so cancelling the dialog cannot mutate the displayed card
    const editableCopy: ContractDefinitionInput = {
      '@id': contractDefinition.id,
      accessPolicyId: contractDefinition.accessPolicyId,
      contractPolicyId: contractDefinition.contractPolicyId,
      assetsSelector: (contractDefinition.assetsSelector ?? []).map((criterion: any) => ({
        operandLeft: criterion.operandLeft,
        operator: criterion.operator,
        operandRight: criterion.operandRight,
      })),
    };

    const dialogRef = this.dialog.open(ContractDefinitionEditorDialog, { data: editableCopy });
    dialogRef.afterClosed().pipe(first()).subscribe((result: { contractDefinition?: ContractDefinitionInput }) => {
      const editedContractDefinition = result?.contractDefinition;
      if (editedContractDefinition) {
        this.contractDefinitionService.updateContractDefinition(editedContractDefinition)
          .subscribe({
            next: () => {
              this.notificationService.showInfo("Contract definition updated");
              this.loadContractDefinitions();
            },
            error: (err) => this.notificationService.showError(this.utilService.getApiErrorMessage(err) ?? "Contract definition cannot be updated"),
          });
      }
    });
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }
}
